<template>
    <div
        class="icon-frame"
        :class="[
            {[`icon-frame--size-${size}`]: size },
            {[`icon-frame--align-x-${alignX}`]: alignX },
            {[`icon-frame--align-y-${alignY}`]: alignY },
            {[`icon-frame--debug`]: debug }
        ]">
        <slot></slot>
    </div>
</template>

<script>

/**
 * Icon frame
 * used as an icon square container.
 *
 * Default icon alignment is center on x axis (change using align property),
 * and on y axis always at the bottom. Default frame size is 16x16 px (change using size property).
 *
 * Tip: set debug to true in order to see the frame container
 *
 * @example
 * ```js
 * import DsIconFrame from '../../atoms';
 *
 * export default {
 *   components: { DsIconFrame },
 *   template: `<div>
 *
 *     <DsIconFrame> icon here </DsIconFrame>
 *
 *     <!-- icon will be left aligned horizontally -->
 *     <DsIconFrame align-x="left"> icon here </DsIconFrame>
 *
 *     <!-- icon will be center aligned vertically -->
 *     <DsIconFrame align-y="center"> icon here </DsIconFrame>
 *
 *     <!-- frame size will be 24px -->
 *     <DsIconFrame size="24"> icon here </DsIconFrame>
 *
 *   </div>`;
 * };
 *```
 */
export default {
    name: 'DsIconFrame',
    props: {
        size: {
            type: String,
            default: '16',
            validator: type => /^16|24|40|48$/.test(type)
        },
        alignX: {
            type: String,
            default: 'center',
            validator: type => /^left|right|center$/.test(type)
        },
        alignY: {
            type: String,
            default: 'bottom',
            validator: type => /^top|bottom|center$/.test(type)
        },
        debug: {
            type: Boolean,
            default: false
        }
    }
};
</script>

<style lang="scss" scoped>
@import '../../foundation/tokens/variables';

$frame-size-16: 16px;
$frame-size-24: 24px;
$frame-size-40: 40px;
$frame-size-48: 48px;

.icon-frame {
    align-items: flex-end;
    display: flex;
    height: $frame-size-16;
    justify-content: center;
    width: $frame-size-16;

    .icon {
        height: auto;
        max-height: 100%;
        max-width: 100%;
        width: auto;
    }

    &--debug {
        background-color: $color-red-050;
    }

    &--size-24 {
        height: $frame-size-24;
        width: $frame-size-24;
    }

    &--size-40 {
        height: $frame-size-40;
        width: $frame-size-40;
    }

    &--size-48 {
        height: $frame-size-48;
        width: $frame-size-48;
    }

    &--align-x-left {
        justify-content: flex-start;
    }

    &--align-x-right {
        justify-content: flex-end;
    }

    &--align-y-top {
        align-items: flex-start;
    }

    &--align-y-center {
        align-items: center;
    }
}
</style>
